/**
 * @file src/api/midiLearnService.js
 * @description The HTTP surface of the MIDI-learn endpoint, and nothing else.
 *
 * One request in, one answer out. What a slice MEANS — which entity it belongs to, how two callers
 * share one read, what to serve when the server cannot be reached, and keeping the local copy in
 * step with an accepted write — is `input/midi/midiSliceSource.js`. Keeping those apart is what
 * lets there be exactly one place that decides where a mapping came from.
 */
import { fetchJsonFromApi } from './httpClient.js';
import { requestEmbeddedAuthToken } from './dataManager/loaders.js';
import { resolveViewerAuth } from '../auth/viewerAuth.js';
import { ensureLoginPrompt, getLoginHref } from '../ui/loginPrompt.js';
import { READ_OUTCOME } from '../config/configurationPack.js';

const ENDPOINT = '/me/users/configurations/midi-learn';

/**
 * Normalises API responses and surfaces auth errors with actionable messaging.
 * @param {Response} response
 * @param {string} authErrorMessage
 * @returns {Promise<object>}
 */
async function handleResponse(response, authErrorMessage) {
  if (response.status === 204) {
    return {};
  }

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      requestEmbeddedAuthToken();
      ensureLoginPrompt({ href: getLoginHref(), text: 'Log in' });
      throw new Error(authErrorMessage);
    }

    let message = `${authErrorMessage} (status ${response.status})`;
    try {
      const data = await response.json();
      if (data?.message) {
        message = data.message;
      }
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  try {
    return await response.json();
  } catch {
    return {};
  }
}

/**
 * What one MIDI-learn GET actually found, in the terms `configurationPack` reasons about.
 *
 * The old reader answered `{}` for a 204, for a 401 and for a body that would not parse. Those are
 * three different facts — "you have no mappings", "we cannot tell who you are", and "the server
 * said something we could not read" — and a mirror that cannot tell them apart either clears a
 * good local copy on one bad response or serves a deleted mapping back forever.
 *
 * @param {Response} response
 * @returns {Promise<{outcome: string, value: object|null, message: string|null}>}
 */
async function readLoadResponse(response) {
  if (response.status === 204 || response.status === 404) {
    return { outcome: READ_OUTCOME.ABSENT, value: null, message: null };
  }

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      requestEmbeddedAuthToken();
      return { outcome: READ_OUTCOME.UNAUTHORIZED, value: null, message: null };
    }

    let message = `Failed to load MIDI mappings (status ${response.status})`;
    try {
      const data = await response.json();
      if (data?.message) {
        message = data.message;
      }
    } catch {
      // A non-JSON error body is still an error; the status is the part that matters.
    }
    return { outcome: READ_OUTCOME.UNAVAILABLE, value: null, message };
  }

  try {
    return { outcome: READ_OUTCOME.NETWORK, value: await response.json(), message: null };
  } catch {
    return { outcome: READ_OUTCOME.INVALID_RESPONSE, value: null, message: null };
  }
}

/**
 * The parameter id a collection's MIDI-scope preference is stored under, inside that collection's
 * own slice — alongside its studio-control mappings rather than in a store of its own.
 *
 * It is NOT a binding, and nothing treats it as one: `ScopedMidiMap.loadSlice` drops any row
 * without a finite `cc`, so it never enters the binding map. It rides this store because the
 * backend already accepts it as-is — `binding` is `Mixed`, the sanitizer passes any
 * string/number/boolean, and `parameterId` is free-form — and because a preference that selects
 * between two sets of mappings belongs with those mappings, on the account, not in browser storage.
 */
export const MIDI_SCOPE_PREFERENCE_PARAM_ID = 'collection-midi-scope';

function buildLoadUrl(scopeRef) {
  if (typeof scopeRef === 'string' && scopeRef) {
    return `${ENDPOINT}?orbiterId=${encodeURIComponent(scopeRef)}`;
  }
  if (scopeRef?.scope && scopeRef?.entityId) {
    return `${ENDPOINT}?scope=${encodeURIComponent(scopeRef.scope)}&entityId=${encodeURIComponent(scopeRef.entityId)}`;
  }
  return ENDPOINT;
}

/**
 * Read MIDI mappings and say what happened, without throwing.
 *
 * This is the read the mirror is built on: it has to distinguish a server that answered from one
 * that could not be reached, because only the first is a reason to change what is on disk. A GET
 * with no network rejects inside `fetch` — there is no offline door for reads, and there should not
 * be one — so a rejection is `unavailable` unless the caller cancelled.
 *
 * @param {string|{scope: string, entityId: string}} [scopeRef]
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{outcome: string, value: object|null, message: string|null}>}
 */
export async function readMidiMappings(scopeRef, { signal } = {}) {
  try {
    // Resolve a bearer when one exists, but do NOT bail out when it is absent: in production the
    // orbiter iframe is usually authenticated by the session cookie alone (fetchJsonFromApi sends
    // `credentials: 'include'`), exactly like the PUT save path. Short-circuiting here meant the
    // GET was never issued, so saved mappings never loaded.
    const { authToken } = await resolveViewerAuth();
    const response = await fetchJsonFromApi(buildLoadUrl(scopeRef), {
      method: 'GET',
      authToken,
      signal,
    });
    return readLoadResponse(response);
  } catch (error) {
    const outcome = error?.name === 'AbortError' ? READ_OUTCOME.ABORTED : READ_OUTCOME.UNAVAILABLE;
    return { outcome, value: null, message: error?.message || null };
  }
}

/**
 * The throwing form, kept for the load-saved-mappings dialog, which wants the full tree plus the
 * name maps and has its own error surface. Everything on the boot path reads through
 * {@link readMidiMappings} instead, so that a failure can fall back to the mirror rather than
 * becoming an empty answer.
 *
 * Only an answer FROM THE SERVER comes back as a tree: a value, or `{}` for a stated absence. This
 * used to throw on an unreachable server alone, so a 401, a cancelled read and a body that would
 * not parse all came back as an empty tree — and a dialog cannot tell an empty tree from an account
 * with no mappings. It listed "no saved mappings" to somebody whose session had simply expired,
 * with no prompt to log back in and nothing to retry.
 *
 * The thrown error carries the outcome, so a caller that wants to say something more specific than
 * "we could not read them" can.
 */
export async function fetchMidiMappings(scopeRef) {
  const { outcome, value, message } = await readMidiMappings(scopeRef);
  if (outcome === READ_OUTCOME.NETWORK) return value || {};
  // The server stating there is nothing IS an empty tree, and the only one.
  if (outcome === READ_OUTCOME.ABSENT) return {};
  if (outcome === READ_OUTCOME.UNAUTHORIZED) {
    // The one outcome the person can do something about. `readMidiMappings` has already asked an
    // embedding host for a fresh token; a standalone session needs the prompt.
    ensureLoginPrompt({ href: getLoginHref(), text: 'Log in' });
  }
  const error = new Error(message || 'Failed to load MIDI mappings');
  error.outcome = outcome;
  throw error;
}

/**
 * Persists a MIDI mapping for the provided scope/entity/parameter pairing.
 * @param {{ scope: string, entityId: string, parameterId: string, binding: object }} payload
 * @returns {Promise<object>}
 */
export async function saveMidiMapping({ scope, entityId, parameterId, binding }) {
  if (!scope || !entityId || !parameterId || !binding) {
    throw new Error('saveMidiMapping requires scope, entityId, parameterId, and binding.');
  }

  const { authToken } = await resolveViewerAuth();
  const response = await fetchJsonFromApi(ENDPOINT, {
    method: 'PUT',
    authToken,
    body: { scope, entityId, parameterId, binding },
  });

  return handleResponse(response, 'Authentication required to save MIDI mappings');
}

/**
 * Removes a MIDI mapping for the provided scope/entity/parameter pairing on the server.
 * @param {{ scope: string, entityId: string, parameterId: string }} payload
 * @returns {Promise<object>}
 */
export async function clearMidiMappingRemote({ scope, entityId, parameterId }) {
  if (!scope || !entityId || !parameterId) {
    throw new Error('clearMidiMappingRemote requires scope, entityId, and parameterId.');
  }

  const { authToken } = await resolveViewerAuth();
  const response = await fetchJsonFromApi(ENDPOINT, {
    method: 'PUT',
    authToken,
    body: { scope, entityId, parameterId, binding: null },
  });

  return handleResponse(response, 'Authentication required to update MIDI mappings');
}
